"use client";

import { useContext, useEffect, useMemo, useRef } from "react";
import { SpacetimeDBProvider, useSpacetimeDB } from "spacetimedb/react";
import {
  AccountRuntimeContext,
  useAccountSession,
} from "@/providers/account-session";
import { getPortalSession } from "@/app/actions";
import { buildPortalSpacetimeBuilder } from "@/lib/spacetime";
import { StaffAccessContext } from "./staff-access";
import {
  markConnected,
  markTokenResolved,
  useBootReady,
} from "@/lib/boot-state";

/** Protected routes wait for the shared runtime; public routes never do. */
export function PortalSpacetimeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { session, resolving } = useAccountSession();
  const runtime = useContext(AccountRuntimeContext);
  useEffect(() => {
    if (!resolving && session) markTokenResolved();
  }, [resolving, session]);
  return <>{runtime && children}</>;
}

export function PortalSessionProvider({
  session,
  children,
  reportBoot = true,
}: {
  session: Awaited<ReturnType<typeof getPortalSession>>;
  children: React.ReactNode;
  reportBoot?: boolean;
}) {
  const { updateSession } = useAccountSession();
  const currentToken = session.token;
  const builder = useMemo(
    () => buildPortalSpacetimeBuilder(currentToken),
    [currentToken],
  );
  return (
    <SpacetimeDBProvider connectionBuilder={builder}>
      <TokenRefresher onFreshSession={updateSession} />
      {reportBoot && <ConnectionReporter />}
      <StaffAccessContext.Provider value={session.staffAccess}>
        {children}
      </StaffAccessContext.Provider>
    </SpacetimeDBProvider>
  );
}

/** Reports the first successful socket connect to the boot store. */
function ConnectionReporter() {
  const { isActive } = useSpacetimeDB();
  useEffect(() => {
    if (isActive) markConnected();
  }, [isActive]);
  return null;
}

function TokenRefresher({
  onFreshSession,
}: {
  onFreshSession: (
    session: Awaited<ReturnType<typeof getPortalSession>>,
  ) => void;
}) {
  const { connectionError } = useSpacetimeDB();
  const refreshing = useRef(false);
  useEffect(() => {
    if (!connectionError || refreshing.current) return;
    refreshing.current = true;
    getPortalSession()
      .then((t) => onFreshSession(t))
      .catch((error) => {
        // Keep the stale builder on a failed refresh — do NOT call
        // onFreshSession here. The SDK's own reconnect backoff will produce a
        // fresh connectionError (a distinct object) on its next attempt,
        // which re-arms this effect (new dep) so we try again then.
        console.warn(
          "PortalSpacetimeProvider: failed to refresh SpacetimeDB token after a connection error",
          error,
        );
      })
      .finally(() => {
        refreshing.current = false;
      });
  }, [connectionError, onFreshSession]);
  return null;
}

export { useSpacetimeDB as usePortalConnection };

/**
 * Boot-once error gate. Before first ready, a connectionError blocks the
 * portal with the full-screen error fallback (retry = reload). AFTER first
 * ready the gate is inert: transient errors heal silently via the SDK's
 * auto-reconnect + TokenRefresher, and content stays mounted (spec §4).
 */
export function SpacetimeGate({
  children,
  errorFallback,
}: {
  children: React.ReactNode;
  errorFallback: (error: Error, retry: () => void) => React.ReactNode;
}) {
  const { connectionError } = useSpacetimeDB();
  const ready = useBootReady();
  if (!ready && connectionError) {
    return (
      <>{errorFallback(connectionError, () => window.location.reload())}</>
    );
  }
  return <>{children}</>;
}
